import React from 'react';
import { View, TouchableOpacity, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { GroupChat } from '../../types/chats';
import { getRelativeTime } from '../../data/mockChatsData';
import { useTheme } from '../../theme/ThemeContext';
import ThemedText from '../common/ThemedText';

interface GroupChatCardProps {
  groupChat: GroupChat;
  onPress: () => void;
}

const GroupChatCard: React.FC<GroupChatCardProps> = ({ groupChat, onPress }) => {
  const { theme } = useTheme();
  
  return (
    <TouchableOpacity style={[styles.card, { backgroundColor: theme.card }]} onPress={onPress} activeOpacity={0.7}>
      <View style={styles.iconContainer}>
        <View style={[styles.groupIcon, { backgroundColor: theme.primary }]}>
          <Ionicons name="people" size={28} color="#000000" />
        </View>
      </View>
      
      <View style={styles.content}>
        <View style={styles.header}>
          <ThemedText style={[styles.clubName, { color: theme.text }]} numberOfLines={1}>{groupChat.clubName}</ThemedText>
          <ThemedText style={[styles.time, { color: theme.textTertiary }]}>{getRelativeTime(groupChat.lastMessageTime)}</ThemedText>
        </View>
        
        <View style={styles.messageRow}>
          <ThemedText 
            style={[
              styles.lastMessage,
              { color: groupChat.unreadCount > 0 ? theme.text : theme.textSecondary },
              groupChat.unreadCount > 0 && styles.unreadMessage
            ]} 
            numberOfLines={1}
          >
            {groupChat.lastMessage}
          </ThemedText>
          {groupChat.unreadCount > 0 && (
            <View style={[styles.unreadBadge, { backgroundColor: theme.primary }]}>
              <ThemedText style={[styles.unreadCount, { color: '#000000' }]}>{groupChat.unreadCount}</ThemedText>
            </View>
          )}
        </View>
        
        <View style={styles.footer}>
          <Ionicons name="people-outline" size={14} color={theme.textSecondary} />
          <ThemedText style={[styles.memberCount, { color: theme.textSecondary }]}>
            {groupChat.memberCount} {groupChat.memberCount === 1 ? 'member' : 'members'}
          </ThemedText>
        </View>
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    marginHorizontal: 16,
    marginBottom: 12,
    borderRadius: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  iconContainer: {
    marginRight: 12,
  },
  groupIcon: {
    width: 50,
    height: 50,
    borderRadius: 25,
    justifyContent: 'center',
    alignItems: 'center',
  },
  content: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  clubName: {
    fontSize: 16,
    fontWeight: '600',
    flex: 1,
    marginRight: 8,
  },
  time: {
    fontSize: 12,
  },
  messageRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
  },
  lastMessage: {
    fontSize: 14,
    flex: 1,
    marginRight: 8,
  },
  unreadMessage: {
    fontWeight: '600',
  },
  unreadBadge: {
    minWidth: 20,
    height: 20,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 6,
  },
  unreadCount: {
    fontSize: 11,
    fontWeight: '600',
  },
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  memberCount: {
    fontSize: 12,
  },
});

export default GroupChatCard;
